import { useEffect, useMemo, useState } from "react";
import { useApi } from "@/lib/api";
import {
  cleanMarkdownBody,
  ColorBlock,
  BigStat,
} from "@/components/page";
import { CalendarClock } from "lucide-react";

type Client = {
  id: string;
  name: string;
  session_count?: number;
  last_session_at?: string | null;
  created_at: string;
};

type Session = {
  id: string;
  client_id: string;
  client_name: string | null;
  scheduled_at: string;
  summary: string | null;
  created_at: string;
};

const dateFmt = new Intl.DateTimeFormat("he-IL", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const todayFmt = new Intl.DateTimeFormat("he-IL", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

const dayFmt = new Intl.DateTimeFormat("he-IL", { weekday: "long" });

function summaryPreview(raw: string): string {
  const cleaned = cleanMarkdownBody(raw);
  const plain = cleaned
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^[>\-*]\s+/gm, "")
    .replace(/[*_`]+/g, "")
    .trim();
  return plain
    .split(/\n+/)
    .map((s) => s.trim())
    .filter((s) => s && !/^[-*_]{3,}$/.test(s))
    .slice(0, 3)
    .join(" · ");
}

export function HomePage() {
  const api = useApi();
  const [clients, setClients] = useState<Client[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cRes, sRes] = await Promise.all([
          api("/api/clients"),
          api("/api/sessions"),
        ]);
        if (cancelled) return;
        if (cRes.ok) setClients((await cRes.json()) as Client[]);
        if (sRes.ok) setSessions((await sRes.json()) as Session[]);
      } catch {
        // soft-fail: placeholders will render
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const today = useMemo(() => todayFmt.format(new Date()), []);
  const dayOfWeek = useMemo(() => dayFmt.format(new Date()), []);

  const activeClients = clients.length > 0 ? clients.length : 15;

  const sessionsThisWeek = useMemo(() => {
    if (sessions.length === 0) return 0;
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay());
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 7);
    return sessions.filter((s) => {
      const t = new Date(s.scheduled_at).getTime();
      return t >= start.getTime() && t < end.getTime();
    }).length;
  }, [sessions]);

  const methodologyCount = 9;
  const instagramDrafts = 2;

  const nextSession = useMemo(() => {
    const now = Date.now();
    return (
      sessions
        .filter((s) => new Date(s.scheduled_at).getTime() > now)
        .sort(
          (a, b) =>
            new Date(a.scheduled_at).getTime() -
            new Date(b.scheduled_at).getTime(),
        )[0] ?? null
    );
  }, [sessions]);

  const recentSessions = useMemo(
    () =>
      [...sessions]
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )
        .slice(0, 5),
    [sessions],
  );

  return (
    <div className="min-h-svh -mx-6 -my-8 px-6 py-10 sm:py-14 bg-stone-50">
      <div className="mx-auto max-w-5xl space-y-6 sm:space-y-7">
        {/* Hero — massive, asymmetric, no card chrome */}
        <header className="pb-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">
            {today}
          </p>
          <h1 className="mt-5 text-5xl sm:text-7xl font-bold tracking-[-0.04em] leading-[0.95] text-stone-900">
            בוקר טוב, לילך.
          </h1>
          <p className="mt-5 max-w-xl text-lg sm:text-xl text-stone-600 leading-snug">
            הנה השבוע שלך, {dayOfWeek}.
          </p>
        </header>

        {/* Featured stat — full-width sage block with poster number */}
        <ColorBlock tint="sage" size="lg">
          <div className="grid sm:grid-cols-[1fr_auto] gap-8 sm:gap-12 items-end">
            <BigStat
              label="מתאמנות פעילות"
              value={loading ? "…" : activeClients}
              size="xl"
              hint="במעקב אקטיבי"
            />
            <div className="grid grid-cols-3 gap-6 sm:gap-10">
              <BigStat
                label="השבוע"
                value={loading ? "…" : sessionsThisWeek}
                size="md"
                hint="מפגשים"
              />
              <BigStat
                label="מתודולוגיה"
                value={methodologyCount}
                size="md"
                hint="כרטיסים"
              />
              <BigStat
                label="טיוטות"
                value={instagramDrafts}
                size="md"
                hint="אינסטגרם"
              />
            </div>
          </div>
        </ColorBlock>

        {/* Two-up: next session (peach) + nothing-yet placeholder space-grid */}
        <div className="grid sm:grid-cols-5 gap-6 sm:gap-7">
          <ColorBlock
            tint="peach"
            size="md"
            eyebrow="המפגש הבא"
            className="sm:col-span-3"
          >
            {nextSession ? (
              <div>
                <p className="text-3xl sm:text-4xl font-bold tracking-tight text-stone-900 leading-tight">
                  {nextSession.client_name ?? "מתאמנת"}
                </p>
                <p className="mt-2 text-sm font-medium text-stone-700/80 tabular-nums" dir="ltr">
                  {dateFmt.format(new Date(nextSession.scheduled_at))}
                </p>
                {nextSession.summary ? (
                  <p className="mt-5 text-base text-stone-700 leading-relaxed line-clamp-3">
                    {summaryPreview(nextSession.summary)}
                  </p>
                ) : null}
              </div>
            ) : (
              <div>
                <p className="text-3xl sm:text-4xl font-bold tracking-tight text-stone-900 leading-tight">
                  לא מתוזמן מפגש.
                </p>
                <p className="mt-4 text-base text-stone-700/85 leading-relaxed max-w-md">
                  ברגע שתתזמני מפגש הוא יופיע כאן עם השעה והסיכום האחרון.
                </p>
              </div>
            )}
          </ColorBlock>

          <ColorBlock
            tint="cream"
            size="md"
            eyebrow="היום"
            className="sm:col-span-2"
          >
            <div className="flex items-center gap-3 text-stone-800">
              <CalendarClock className="h-6 w-6 opacity-60" />
              <p className="text-2xl sm:text-3xl font-bold tracking-tight">
                {dayOfWeek}
              </p>
            </div>
            <p className="mt-3 text-sm text-stone-700/85 leading-relaxed">
              עוד יום בעבודה — קחי שתי דקות לרשום מה הכי חשוב להיום.
            </p>
          </ColorBlock>
        </div>

        {/* Recent activity — large cream block, dated journal entries */}
        <ColorBlock
          tint="stone"
          size="lg"
          eyebrow="פעילות אחרונה"
          aside={
            <span className="text-[11px] uppercase tracking-[0.18em]">
              {recentSessions.length} מפגשים
            </span>
          }
        >
          {recentSessions.length === 0 ? (
            <p className="text-base text-stone-600 leading-relaxed max-w-md">
              עוד אין פעילות לתעד. מפגשים, סיכומים ותוכן שתיצרי יופיעו כאן בסדר
              כרונולוגי.
            </p>
          ) : (
            <ol className="divide-y divide-stone-300/50">
              {recentSessions.map((s) => (
                <li
                  key={s.id}
                  className="grid grid-cols-[7rem_1fr] gap-6 sm:gap-10 py-4 first:pt-0 last:pb-0"
                >
                  <span
                    className="text-xs font-medium text-stone-500 tabular-nums pt-1.5"
                    dir="ltr"
                  >
                    {dateFmt.format(new Date(s.scheduled_at))}
                  </span>
                  <div className="min-w-0">
                    <p className="text-lg sm:text-xl font-semibold tracking-tight text-stone-900 truncate">
                      {s.client_name ?? "מפגש"}
                    </p>
                    {s.summary ? (
                      <p className="mt-1 text-sm text-stone-600 leading-relaxed line-clamp-2">
                        {summaryPreview(s.summary)}
                      </p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </ColorBlock>
      </div>
    </div>
  );
}
